if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.selectors = {
          container: ".main-product--wrapper",
          slider: ".media-gallery__inner",
          sliderPagination: ".swiper-pagination",
          sliderPrevEl: ".swiper-button-prev",
          sliderNextEl: ".swiper-button-next",
          navSlider: ".nav-swiper-container",
          medias: [".product-media--item"],
          mediaWrapper: ".product-media--wrapper",
          mediaZoomIns: [".product-media__zoom-in"],
          videos: [".media-video"],
          featuredImage: ".product-media",
          galleryList: ".media-gallery__list",
          galleryInner: ".media-gallery__inner",
        };

        this.navSlider = false;
        this.slider = false;
        this.currentMediaGroup = "image";
      }

      get context() {
        return this.dataset.context;
      }

      connectedCallback() {
        this.init();
      }

      init() {
        this.section = this.closest('product-info');
        this.domNodes = queryDomNodes(this.selectors, this);
        this.enableVideoAutoplay = this.dataset.enableVideoAutoplay === "true";
        this.enableImageZoom = this.dataset.enableImageZoom === "true";
        this.enableVariantGroupImages = this.dataset.enableVariantGroupImages === "true";
        this.onlyMedia = this.dataset.onlyMedia === "true";
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.variantPicker = this.section.querySelector("variant-picker");

        this.initMediaTabs();
        this.initMediaObserver();
        this.setSliderOptions();

        const mql = window.matchMedia(Theme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.handlePhotoswipe();
        };

        this.handleAutoplayVideo();

        this.removeAttribute("data-media-loading");
        this.domNodes.galleryInner.style.opacity = 1;
      }

      initMediaTabs() {
        const tabsWrapper = this.querySelector(".product-media__tabs-wrapper");
        if (!tabsWrapper) return;

        this.mediaTabs = new Theme.Tabs(tabsWrapper, (target) => {
          const group = target ? target.dataset.mediaTab : null;
          if (group && group !== this.currentMediaGroup) {
            this.filterMedia(group);
          }
        });

        const activeTab = this.querySelector(".product-media__tab.active") || this.querySelector(".product-media__tab");
        if (activeTab && activeTab.dataset.mediaTab) {
          this.filterMedia(activeTab.dataset.mediaTab, true);
        }
      }

      initMediaObserver() {
        if (!this.domNodes || !this.domNodes.galleryList) return;

        this.mediaObserver = new MutationObserver((mutations) => {
          if (this._isFiltering) return;

          let hasNewNodes = false;
          for (const mutation of mutations) {
            if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
              for (const node of mutation.addedNodes) {
                if (node.nodeType === 1 && node.classList.contains("product-media--item") && !node.classList.contains("swiper-slide-duplicate")) {
                  hasNewNodes = true;
                  break;
                }
              }
            }
            if (hasNewNodes) break;
          }

          if (hasNewNodes) {
            this.filterMedia(this.currentMediaGroup);
          }
        });

        this.mediaObserver.observe(this.domNodes.galleryList, { childList: true });
      }

      onVariantUpdate(variant) {
        this.filterMedia(this.currentMediaGroup);

        if (this.currentMediaGroup === "image") {
          this.setActiveMedia(variant);
        }

        if (this.enableImageZoom) {
          this.handlePhotoswipe();
        }
      }

      filterMedia(group, isInitial = false) {
        if (!group) return;
        this.currentMediaGroup = group;
        this._isFiltering = true;

        if (!isInitial && typeof window.pauseAllMedia === "function") {
          window.pauseAllMedia(this);
        }

        if (!isInitial) {
          if (this.slider && typeof this.slider.destroy === "function") {
            this.slider.destroy(true, true);
            this.slider = false;
          }
          if (this.navSlider && typeof this.navSlider.destroy === "function") {
            this.navSlider.destroy(true, true);
            this.navSlider = false;
          }
        }

        const allSlides = this.querySelectorAll(".media-gallery__list .product-media--item");
        allSlides.forEach((slide) => {
          const match = slide.dataset.mediaGroup === group;
          if (match) {
            slide.classList.remove("hidden");
            slide.classList.add("swiper-slide");
          } else {
            slide.classList.add("hidden");
            slide.classList.remove("swiper-slide");
          }
        });

        const allThumbs = this.querySelectorAll(".nav-swiper-container [data-media-group]");
        let visibleThumbsCount = 0;
        allThumbs.forEach((thumb) => {
          const match = thumb.dataset.mediaGroup === group;
          if (match) {
            thumb.classList.remove("hidden");
            thumb.classList.add("swiper-slide");
            visibleThumbsCount++;
          } else {
            thumb.classList.add("hidden");
            thumb.classList.remove("swiper-slide");
          }
        });

        const thumbContainer = this.querySelector(".product-media--slider__thumbnails");
        if (thumbContainer) {
          if (visibleThumbsCount <= 1) {
            thumbContainer.classList.add("is-single-thumb");
          } else {
            thumbContainer.classList.remove("is-single-thumb");
          }
        }

        if (!isInitial) {
          this.domNodes = queryDomNodes(this.selectors, this);
          this.updateMediaLayout();
          this.handleAutoplayVideo();
        }

        this._isFiltering = false;
      }

      updateMediaLayout() {
        if (Theme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      setSliderOptions() {
        const { sliderPagination, sliderNextEl: nextEl, sliderPrevEl: prevEl } = this.domNodes;

        const visibleSlides = this.querySelectorAll(".media-gallery__list .product-media--item.swiper-slide");
        const canLoop = visibleSlides.length > 1 && !this.enableVariantGroupImages;

        this.sliderOptions = {
          autoHeight: true,
          navigation: { nextEl, prevEl },
          threshold: 2,
          loop: canLoop,
          pagination: {
            el: sliderPagination,
            clickable: true,
            type: "bullets",
          },
          on: {
            init: () => {
              this.domNodes.slider.style.opacity = 1;
              this.domNodes = queryDomNodes(this.selectors, this.section);
              requestAnimationFrame(() => {
                window.dispatchEvent(new Event("resize"));
              });
            }
          }
        };

        this.thumbsOptions = {
          loop: false,
          slidesPerView: 6.5,
          freeMode: true,
          spaceBetween: 10,
          threshold: 2,
          watchSlidesVisibility: true,
          watchSlidesProgress: true,
          on: {
            init: () => {
              if (this.domNodes.navSlider) {
                this.domNodes.navSlider.style.opacity = 1;
              }
            }
          }
        };
      }

      initSlider() {
        if (typeof this.slider !== 'object') {
          const { slider, navSlider, galleryList } = this.domNodes;

          this.setSliderOptions();

          const visibleThumbs = navSlider ? navSlider.querySelectorAll('.swiper-slide:not(.hidden)') : [];
          this.navSlider = (navSlider && visibleThumbs.length > 0) ? new ThemeLibs.Swiper(navSlider, this.thumbsOptions) : false;

          if (this.navSlider) {
            this.sliderOptions.thumbs = {
              swiper: this.navSlider
            };
          } else {
            delete this.sliderOptions.thumbs;
          }

          // Initialize the main slider
          slider.classList.add('swiper-container');
          galleryList.classList.remove('grid');
          galleryList.classList.add('swiper-wrapper');

          this.slider = new ThemeLibs.Swiper(slider, this.sliderOptions);

          // Handle slide change for variant group images
          if (!this.enableVariantGroupImages) this.handleSlideChange();

          const activeSlide = this.slider.slides && this.slider.slides[this.slider.activeIndex || 0];
          if (activeSlide && activeSlide.dataset.mediaType === "model") {
            this.toggleSliderDraggableState(false);
          }
        }
      }

      destroySlider() {
        const { slider, galleryList } = this.domNodes;

        slider.classList.remove('swiper-container');
        galleryList.classList.add('grid');
        galleryList.classList.remove('swiper-wrapper');

        if (typeof this.slider === 'object') {
          this.slider.destroy();
          this.slider = false;
        }
      }

      handleSlideChange() {
        if (!this.slider) return;

        this.slider.on("slideChange", (swiper) => {
          window.pauseAllMedia(this);

          const { slides, activeIndex } = swiper;
          const activeSlide = slides[activeIndex];
          if (activeSlide) this.playActiveMedia(activeSlide);

          // Determine visible slides based on layout
          const visibleSlides = [activeIndex];

          // Check if any of the visible slides have a model media type
          const hasModelMediaType = visibleSlides.some((index) => {
            const slide = slides[index];
            return slide && slide.dataset.mediaType === "model";
          });

          // Toggle slider's draggable state based on media type
          this.toggleSliderDraggableState(!hasModelMediaType);
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.slider.allowTouchMove !== isDraggable) {
          this.slider.allowTouchMove = isDraggable;
        }
      }

      handleAutoplayVideo() {
        const playVideo = (mediaElement) => {
          const mediaType = mediaElement.dataset.mediaType;
          if (mediaType === "video" || mediaType === "external_video") {
            const deferredMedia = mediaElement.querySelector("deferred-media");
            const autoplay = deferredMedia && deferredMedia.dataset.autoPlay === "true";
            if (autoplay) deferredMedia.loadContent(false);
          }
        };

        if (this.slider) {
          const { slides, activeIndex } = this.slider;
          const activeSlide = slides[activeIndex];
          if (activeSlide) playVideo(activeSlide);
        } else {
          this.domNodes.medias.forEach(playVideo);
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector("deferred-media");
        if (!deferredMedia || deferredMedia.dataset.autoPlay !== "true") return;

        const playMedia = (element) => {
          if (element.classList.contains("js-youtube") || element.classList.contains("js-vimeo")) {
            const platform = element.classList.contains("js-youtube") ? "youtube" : "vimeo";
            const param = platform === "youtube" ? "mute=1" : "muted=1";
            const symbol = element.src.includes("?") ? "&" : "?";
            element.src += `${symbol}autoplay=1&${param}`;
          } else {
            element.play();
          }
        };

        if (!deferredMedia.hasAttribute("loaded")) {
          deferredMedia.loadContent(false);
        }

        const deferredElement = deferredMedia.querySelector("video, model-viewer, iframe");
        playMedia(deferredElement);
      }

      handlePhotoswipe() {
        const medias = [...this.querySelectorAll(".product-media--item:not(.swiper-slide-duplicate)")];

        const data = medias.map((media) => {
          const { mediaType, index: id } = media.dataset;

          if (mediaType === "image") {
            const {
              mediaSrc: src,
              mediaWidth,
              mediaHeight,
              mediaAlt: alt,
            } = media.querySelector(".product-media").dataset;
            return {
              src,
              width: parseInt(mediaWidth),
              height: parseInt(mediaHeight),
              alt,
              id,
            };
          } else {
            return {
              html: `<div class="pswp__${mediaType}">${media.innerHTML}</div>`,
              type: mediaType,
              id,
            };
          }
        });

        const options = {
          dataSource: data,
          bgOpacity: 1,
          close: false,
          zoom: false,
          arrowNext: false,
          arrowPrev: false,
          counter: false,
          preloader: false,
          pswpModule: ThemeLibs.PhotoSwipeLightbox.PhotoSwipe,
        };
        this.lightbox = new ThemeLibs.PhotoSwipeLightbox(options);

        this.lightbox.addFilter("thumbEl", (thumbEl, { id }, index) => {
          return this.querySelector(`[data-index="${id}"]:not(.swiper-slide-duplicate) img`) || thumbEl;
        });

        this.lightbox.addFilter("placeholderSrc", (placeholderSrc, { data: { id } }) => {
          const el = this.querySelector(`[data-index="${id}"]:not(.swiper-slide-duplicate) img`);
          return el ? el.src : placeholderSrc;
        });

        this.lightbox.on("change", () => {
          window.pauseAllMedia(this);
          if (this.slider) {
            const { currIndex } = this.lightbox.pswp;
            const slideMethod = this.enableVariantGroupImages ? "slideTo" : "slideToLoop";
            this.slider[slideMethod](currIndex, 100, false);
          }
        });

        this.lightbox.on("pointerDown", (e) => {
          if (this.lightbox.pswp.currSlide.data.type === "model") {
            e.preventDefault();
          }
        });

        // UI elements
        const uiElements = [
          {
            name: "m-close",
            order: 11,
            isButton: true,
            html: '<svg class="svg-icon--large" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>',
            onClick: (event, el, pswp) => pswp.close(),
          },
          {
            name: "m-arrowNext",
            order: 12,
            isButton: true,
            html: '<svg fill="currentColor" width="14px" height="14px" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M218.101 38.101L198.302 57.9c-4.686 4.686-4.686 12.284 0 16.971L353.432 230H12c-6.627 0-12 5.373-12 12v28c0 6.627 5.373 12 12 12h341.432l-155.13 155.13c-4.686 4.686-4.686 12.284 0 16.971l19.799 19.799c4.686 4.686 12.284 4.686 16.971 0l209.414-209.414c4.686-4.686 4.686-12.284 0-16.971L235.071 38.101c-4.686-4.687-12.284-4.687-16.97 0z"></path></svg>',
            onClick: (event, el, pswp) => pswp.next(),
          },
          {
            name: "m-arrowPrev",
            order: 10,
            isButton: true,
            html: '<svg width="14px" height="14px" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M229.9 473.899l19.799-19.799c4.686-4.686 4.686-12.284 0-16.971L94.569 282H436c6.627 0 12-5.373 12-12v-28c0-6.627-5.373-12-12-12H94.569l155.13-155.13c4.686-4.686 4.686-12.284 0-16.971L229.9 38.101c-4.686-4.686-12.284-4.686-16.971 0L3.515 247.515c-4.686 4.686-4.686 12.284 0 16.971L212.929 473.9c4.686 4.686 12.284 4.686 16.971-.001z"></path></svg>',
            onClick: (event, el, pswp) => pswp.prev(),
          },
        ];

        this.lightbox.on("uiRegister", () => {
          this.lightbox.pswp.ui.registerElement(uiElements[0]);
          if (!this.onlyMedia) {
            uiElements.slice(1).forEach((element) => this.lightbox.pswp.ui.registerElement(element));
          }
        });

        this.lightbox.init();

        const handleMediaClick = (e, media) => {
          e.preventDefault();
          const isImage = media.classList.contains("media-type-image");
          const isZoomButton = e.target.closest(this.selectors.mediaZoomIns[0]);

          if (isImage || isZoomButton) {
            const index = Number(media.dataset.index) || 0;
            this.lightbox.loadAndOpen(index);
          }
        };

        addEventDelegate({
          selector: this.selectors.medias[0],
          context: this,
          handler: handleMediaClick,
        });
      }

      setActiveMedia(variant) {
        if (!variant) return;

        if (this.currentMediaGroup === "image") {
          if (this.slider) {
            this.setActiveMediaForSlider(variant);
          } else {
            this.handleGalleryMode(variant);
          }
        }
      }

      setActiveMediaForSlider(variant) {
        if (variant.featured_media && this.slider && this.slider.wrapperEl) {
          const visibleSlides = [...this.slider.wrapperEl.querySelectorAll('.product-media--item.swiper-slide:not(.swiper-slide-duplicate)')];
          const targetIndex = visibleSlides.findIndex(slide => slide.dataset.mediaId == variant.featured_media.id);
          if (targetIndex !== -1) {
            const slideMethod = this.slider.params && this.slider.params.loop ? "slideToLoop" : "slideTo";
            this.slider[slideMethod](targetIndex);
          }
        }
      }

      handleGalleryMode(variant) {
        if (this.context !== "featured-product" && variant && variant.featured_media) {
          const selectedMedia = this.querySelector(`.media-gallery__list [data-media-id="${variant.featured_media.id}"]`);
          if (selectedMedia) {
            this.scrollIntoView(selectedMedia);
          }
        }
      }

      scrollIntoView(selectedMedia) {
        selectedMedia.scrollIntoView({
          behavior: "smooth",
        });
      }
    }
  );
}
